import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const VideoSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [selectedField, setSelectedField] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [boost, setBoost] = useState(1);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch options based on selected field
  useEffect(() => {
    const fetchOptions = async () => {
      if (!selectedField) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let data;
        if (selectedField === 'video_orientations') {
          data = await fetchCachedList('/search/frequent_values/farcaster-items/video_orientations');
        } else if (selectedField === 'video_languages') {
          data = await fetchCachedList('/search/frequent_values/farcaster-items/video_languages');
        }
        
        setOptions(data || []);
      } catch (err) {
        console.error(`Failed to fetch ${selectedField} options:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [selectedField, fetchCachedList]);

  const handleVideoAction = (actionType) => {
    if (!currentValue || !selectedField) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: selectedField,
      value: [currentValue]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentValue(''); // Reset after adding
  };

  const getFieldLabel = (field) => {
    switch (field) {
      case 'video_orientations':
        return 'Video Orientation';
      case 'video_languages':
        return 'Video Language';
      default:
        return field;
    }
  };

  return (
    <Box>
      {/* Field Selection */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Video Filter Field</InputLabel>
        <Select
          value={selectedField}
          label="Video Filter Field"
          onChange={(e) => {
            setSelectedField(e.target.value);
            setCurrentValue(''); // Reset value when field changes
          }}
        >
          <MenuItem value="video_orientations">Video Orientations</MenuItem>
          <MenuItem value="video_languages">Video Languages</MenuItem>
        </Select>
      </FormControl>

      {/* Value Selection - only show if field is selected */}
      {selectedField && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{getFieldLabel(selectedField)}</InputLabel>
            <Select
              value={currentValue}
              label={getFieldLabel(selectedField)}
              onChange={(e) => setCurrentValue(e.target.value)}
              disabled={loading}
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label || option.id} ({option.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Boost Input */}
          {boostInput && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Boost"
              type="number"
              value={boost}
              onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
              inputProps={{ min: 0, step: 0.1 }}
              disabled={loading}
            />
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleVideoAction('include')}
              disabled={!currentValue || loading}
            >
              <AddIcon />
            </Button>
            
            {boostInput && (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleVideoAction('boost')}
                disabled={!currentValue || loading}
              >
                <TrendingUpIcon />
              </Button>
            )}
            
            <Button
              variant="contained"
              color="error"
              onClick={() => handleVideoAction('exclude')}
              disabled={!currentValue || loading}
            >
              <RemoveIcon />
            </Button>
          </Box>
        </>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={{ py: 2 }}>
          <Alert severity="error">
            Failed to load {selectedField}: {error}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default VideoSection;

